// Painel de emojis do ChatPage — escolher pra escrever ou pra reagir.
//
// Out/26. O ícone de emoji no rodapé do chat existia e não abria nada: era
// um `<Box>` dentro de um Tooltip, sem `onClick`. Este componente é o que
// faltava atrás dele.
//
// Dois modos, um componente só, porque é a MESMA grade e a mesma busca:
//
//   modo="escrita"  insere no campo de mensagem (rodapé do chat)
//   modo="reacao"   manda a reação pra bolha. Mostra as 6 rápidas em
//                   destaque e marca a que já está aplicada — clicar nela
//                   de novo remove, como no WhatsApp.
//
// O catálogo, a busca sem acento e os recentes moram em
// `services/emojis.js` (inclusive o porquê de ser lista própria e não
// biblioteca de terceiro).
//
// Popover do MUI em vez de posicionamento à mão: clique fora, Escape,
// foco e o reposicionamento quando não cabe na tela vêm de graça — e os
// quatro são exatamente onde painel flutuante feito na mão costuma
// quebrar.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Popover, Tooltip, Typography } from '@mui/material';
import { FiClock, FiSearch, FiX } from 'react-icons/fi';
import {
  CATEGORIAS, REACOES_RAPIDAS, buscar, lerRecentes, registrarUso,
} from '../services/emojis';

const C = {
  card:    'var(--ch-card)',
  surf:    'var(--ch-surf)',
  inset:   'var(--ch-inset)',
  bdr:     'var(--ch-bdr)',
  bdr2:    'var(--ch-bdr2)',
  green:   'var(--ch-green)',
  greenBg: 'var(--ch-green-bg)',
  greenB:  'var(--ch-green-b)',
  greenD:  'var(--ch-green-d)',
  text:    'var(--ch-text)',
  text2:   'var(--ch-text2)',
  text3:   'var(--ch-text3)',
};
const FONT_TXT = "'Syne', sans-serif";

// 8 colunas de 34px + respiro. Largura fixa porque a grade é fixa:
// painel que reflui enquanto a pessoa procura um emoji faz o alvo fugir
// do cursor.
const LARGURA = 318;
const ABA_RECENTES = 'recentes';

// Emoji que representa cada categoria na fita de abas. Sem rótulo de
// texto: nove palavras lado a lado não caberiam em 318px, e o desenho
// identifica mais rápido. O nome vai no Tooltip.
const ICONE_ABA = {
  rostos: '😀', gestos: '👋', coracoes: '❤️', natureza: '🌿',
  comida: '🍕', atividades: '⚽', lugares: '✈️', objetos: '💡',
  simbolos: '✅',
};

const botaoEmojiSx = (marcado = false) => ({
  width: 34, height: 34, flexShrink: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  // 1.15rem: o emoji tem que ser grande o bastante pra ser reconhecido
  // de relance, que é como se escolhe emoji.
  fontSize: '1.15rem', lineHeight: 1,
  border: `1px solid ${marcado ? C.greenB : 'transparent'}`,
  bgcolor: marcado ? C.greenBg : 'transparent',
  borderRadius: '8px', cursor: 'pointer', p: 0,
  transition: 'background-color 0.12s, transform 0.12s',
  '&:hover': { bgcolor: C.inset, transform: 'scale(1.12)' },
  '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: -1 },
});

export default function PainelEmoji({
  open,
  anchorEl,
  onClose,
  onEscolher,
  modo = 'escrita',
  atual = null,
}) {
  const [termo, setTermo] = useState('');
  const [aba, setAba] = useState(CATEGORIAS[0].id);
  const [recentes, setRecentes] = useState([]);
  const buscaRef = useRef(null);

  const reagindo = modo === 'reacao';

  // Relê os recentes a cada abertura: outra aba do painel pode ter usado
  // emoji nesse meio-tempo, e a lista vive no localStorage.
  //
  // Abrir NA aba de recentes só vale pra quem está escrevendo. Reagindo, as
  // 6 rápidas logo acima já são o atalho, e cair numa aba com dois ou três
  // emojis deixava a grade quase vazia — o painel parecia quebrado.
  useEffect(() => {
    if (!open) return;
    const guardados = lerRecentes();
    setRecentes(guardados);
    setTermo('');
    setAba(!reagindo && guardados.length ? ABA_RECENTES : CATEGORIAS[0].id);
  }, [open, reagindo]);

  const resultados = useMemo(() => buscar(termo), [termo]);
  const buscando = termo.trim().length > 0;

  const escolher = (emoji) => {
    setRecentes(registrarUso(emoji));
    onEscolher(emoji);
  };

  // Em modo reação, clicar no emoji que já está aplicado REMOVE — é o
  // gesto do WhatsApp, e sem ele não haveria como desfazer.
  const aoClicar = (emoji) => {
    if (reagindo && atual === emoji) {
      onEscolher('');
      return;
    }
    escolher(emoji);
  };

  const grade = buscando
    ? resultados.map(r => [r.emoji, r.nome])
    : aba === ABA_RECENTES
      ? recentes.map(e => [e, ''])
      : (CATEGORIAS.find(c => c.id === aba)?.itens || []);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      // Pra que lado o painel abre, e isto não é detalhe: o botão de
      // escrever vive no RODAPÉ, então ele só cabe pra cima; o gatilho de
      // reagir vive numa bolha que pode estar em qualquer altura, e
      // forçá-lo pra cima jogava o painel no topo da tela, longe da
      // mensagem e por cima do cabeçalho da conversa. Abrindo pra baixo e
      // centrado no gatilho ele nasce colado na bolha — e o MUI vira
      // sozinho quando não há espaço embaixo.
      anchorOrigin={
        reagindo
          ? { vertical: 'bottom', horizontal: 'center' }
          : { vertical: 'top', horizontal: 'left' }
      }
      transformOrigin={
        reagindo
          ? { vertical: 'top', horizontal: 'center' }
          : { vertical: 'bottom', horizontal: 'left' }
      }
      slotProps={{
        paper: {
          sx: {
            width: LARGURA, bgcolor: C.card,
            border: `1px solid ${C.bdr}`, borderRadius: '12px',
            boxShadow: '0 10px 34px rgba(15,28,50,0.18)',
            overflow: 'hidden',
            // Respiro do lado por onde o painel nasce.
            ...(reagindo ? { mt: 0.8 } : { mb: 1 }),
          },
        },
      }}
    >
      {/* ─── Reações rápidas ─────────────────────────────────────────
          Só no modo reação, e no TOPO: 99% das reações são uma destas
          seis, e fazer a pessoa caçar o 👍 numa grade de 700 pra
          responder "ok" seria trocar um clique por dez. */}
      {reagindo && (
        <Box sx={{
          p: '10px 12px', borderBottom: `1px solid ${C.bdr}`, bgcolor: C.surf,
          display: 'flex', alignItems: 'center', gap: 0.3,
        }}>
          {REACOES_RAPIDAS.map(emoji => (
            <Box
              key={emoji}
              component="button"
              type="button"
              aria-label={`Reagir com ${emoji}`}
              aria-pressed={atual === emoji}
              onClick={() => aoClicar(emoji)}
              sx={{ ...botaoEmojiSx(atual === emoji), width: 38, height: 38, fontSize: '1.3rem' }}
            >
              {emoji}
            </Box>
          ))}
          {/* Tirar a reação só aparece quando existe uma pra tirar. */}
          {atual ? (
            <Tooltip title="Tirar minha reação">
              <Box
                component="button"
                type="button"
                aria-label="Tirar minha reação"
                onClick={() => onEscolher('')}
                sx={{
                  ml: 'auto', width: 28, height: 28, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${C.bdr}`, bgcolor: C.card, color: C.text3,
                  borderRadius: '50%', cursor: 'pointer', p: 0,
                  '&:hover': { borderColor: C.bdr2, color: C.text },
                }}
              >
                <FiX size={12} />
              </Box>
            </Tooltip>
          ) : null}
        </Box>
      )}

      {/* ─── Busca ──────────────────────────────────────────────────── */}
      <Box sx={{ p: '10px 12px 8px' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.8,
          bgcolor: C.surf, border: `1px solid ${C.bdr}`,
          borderRadius: '8px', p: '6px 10px',
          '&:focus-within': { borderColor: C.greenB },
        }}>
          <FiSearch size={13} style={{ color: 'var(--ch-text3)', flexShrink: 0 }} />
          <Box
            component="input"
            ref={buscaRef}
            autoFocus
            value={termo}
            onChange={e => setTermo(e.target.value)}
            placeholder="Buscar emoji…"
            aria-label="Buscar emoji"
            sx={{
              flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none',
              fontFamily: FONT_TXT, fontSize: '0.78rem', color: C.text,
              '&::placeholder': { color: C.text3 },
            }}
          />
          {termo ? (
            <Box
              component="button"
              type="button"
              aria-label="Limpar busca"
              onClick={() => { setTermo(''); buscaRef.current?.focus(); }}
              sx={{
                display: 'flex', border: 'none', background: 'none', p: 0,
                color: C.text3, cursor: 'pointer', '&:hover': { color: C.text },
              }}
            >
              <FiX size={13} />
            </Box>
          ) : null}
        </Box>
      </Box>

      {/* ─── Abas ───────────────────────────────────────────────────────
          Escondidas durante a busca: o resultado já atravessa as
          categorias, então a fita marcaria uma aba que não é a que está
          na tela. */}
      {!buscando && (
        <Box sx={{
          display: 'flex', gap: 0.2, px: '8px', pb: '6px',
          borderBottom: `1px solid ${C.bdr}`, overflowX: 'auto',
          '&::-webkit-scrollbar': { height: 0 },
        }}>
          {recentes.length > 0 && (
            <Tooltip title="Usados recentemente">
              <Box
                component="button"
                type="button"
                aria-label="Usados recentemente"
                aria-pressed={aba === ABA_RECENTES}
                onClick={() => setAba(ABA_RECENTES)}
                sx={abaSx(aba === ABA_RECENTES)}
              >
                <FiClock size={13} />
              </Box>
            </Tooltip>
          )}
          {CATEGORIAS.map(cat => (
            <Tooltip key={cat.id} title={cat.nome}>
              <Box
                component="button"
                type="button"
                aria-label={cat.nome}
                aria-pressed={aba === cat.id}
                onClick={() => setAba(cat.id)}
                sx={abaSx(aba === cat.id)}
              >
                {ICONE_ABA[cat.id]}
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}

      {/* ─── Grade ──────────────────────────────────────────────────── */}
      <Box sx={{
        // Altura fixa pra o painel não pular de tamanho ao trocar de
        // categoria. Menor reagindo: ali o alvo quase sempre é uma das 6
        // rápidas logo acima, e um painel alto nascendo de uma bolha no
        // meio da conversa tapa a conversa inteira.
        height: reagindo ? 170 : 238,
        overflowY: 'auto', p: '8px 10px',
        display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: 0.2,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: C.bdr2, borderRadius: 4 },
      }}>
        {grade.length === 0 ? (
          <Box sx={{
            width: '100%', height: '100%', display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 0.6, px: 2, textAlign: 'center',
          }}>
            <Typography sx={{
              fontFamily: FONT_TXT, fontSize: '0.78rem', fontWeight: 700, color: C.text2,
            }}>
              Nenhum emoji com “{termo.trim()}”
            </Typography>
            <Typography sx={{ fontFamily: FONT_TXT, fontSize: '0.72rem', color: C.text3 }}>
              Procure pelo que o emoji mostra — “bolo”, “carro”, “obrigado”.
            </Typography>
          </Box>
        ) : (
          grade.map(([emoji, nome], i) => (
            <Tooltip key={`${emoji}-${i}`} title={nome || ''} disableHoverListener={!nome}>
              <Box
                component="button"
                type="button"
                aria-label={nome ? `${nome} ${emoji}` : emoji}
                aria-pressed={reagindo && atual === emoji}
                onClick={() => aoClicar(emoji)}
                sx={botaoEmojiSx(reagindo && atual === emoji)}
              >
                {emoji}
              </Box>
            </Tooltip>
          ))
        )}
      </Box>
    </Popover>
  );
}

const abaSx = (ativa = false) => ({
  width: 30, height: 28, flexShrink: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: '0.95rem', lineHeight: 1, p: 0,
  border: 'none', background: 'none', cursor: 'pointer',
  borderBottom: `2px solid ${ativa ? C.green : 'transparent'}`,
  color: ativa ? C.greenD : C.text3,
  opacity: ativa ? 1 : 0.65,
  transition: 'opacity 0.12s',
  '&:hover': { opacity: 1 },
  '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: -2 },
});
